import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';

/**
 * Isolated delete button component - reads from ref via fast polling, no parent re-renders
 * Only appears when rows are selected
 */
const DeleteButton: React.FC<{
  selectedRowsRef: React.MutableRefObject<Set<number>>;
  onDelete: () => void;
  disabled: boolean;
}> = ({ selectedRowsRef, onDelete, disabled }) => {
  const [hasSelected, setHasSelected] = useState(false);
  const prevSizeRef = useRef(0);

  // Fast polling to check ref - only updates state when crossing threshold
  useEffect(() => {
    // Check immediately
    const initialSize = selectedRowsRef.current.size;
    prevSizeRef.current = initialSize;
    setHasSelected(initialSize > 0);

    // Poll every 50ms for fast response (lightweight check)
    const intervalId = setInterval(() => {
      const currentSize = selectedRowsRef.current.size;
      const prevSize = prevSizeRef.current;
      
      // Only update state when crossing threshold (0 to >0 or >0 to 0)
      if ((prevSize === 0 && currentSize > 0) || (prevSize > 0 && currentSize === 0)) {
        setHasSelected(currentSize > 0);
      }
      
      prevSizeRef.current = currentSize;
    }, 50);

    return () => clearInterval(intervalId);
  }, [selectedRowsRef]);

  if (!hasSelected) {
    return null;
  }

  return (
    <Button
      variant="contained"
      color="error"
      startIcon={<DeleteIcon />}
      onClick={onDelete}
      disabled={disabled}
    >
      Delete
    </Button>
  );
};

export default DeleteButton;

